import dotProp from 'dot-prop-immutable';
import type { Action } from '../typings/reducers';

type Image = { _id: string } & Record<string, unknown>;
type Gallery = { _id: string } & Record<string, unknown>;
type Collection = { _id: string; images: Image[]; galleries: Gallery[] } & Record<string, unknown>;
type CollectionsState = {
  scrappbookTemplates: unknown[];
  ids: string[];
  initialCollectionsAmount: number | null;
  byId: Record<string, Collection>;
};

export default function (
  state: CollectionsState = {
    scrappbookTemplates: [],
    ids: [],
    initialCollectionsAmount: null,
    byId: {}
  },
  action: Action
): CollectionsState {
  switch (action.type) {
    case 'SET_SCRAPPBOOK_TEMPLATES': {
      return { ...state, scrappbookTemplates: action.payload };
    }
    case 'SET_COLLECTION_IDS': {
      return { ...state, ids: action.payload };
    }
    case 'SET_INITIAL_COLLECTIONS_AMOUNT': {
      return { ...state, initialCollectionsAmount: action.payload };
    }
    case 'ADD_COLLECTION': {
      const collection: Collection = action.payload;

      return { ...state, byId: { ...state.byId, [collection._id]: collection } };
    }
    case 'REMOVE_IMAGE_FROM_COLLECTION': {
      const { image, collection_id } = action.payload as { image: Image; collection_id: string };
      const col = state.byId[collection_id] as Collection;

      if (col) {
        return {
          ...state,
          byId: {
            ...state.byId,
            [collection_id]: {
              ...col,
              images: col.images.filter((i: Image) => i._id !== image._id)
            }
          }
        };
      }

      return state;
    }
    case 'ADD_IMAGE_TO_COLLECTION': {
      const { image, collection_id } = action.payload as { image: Image; collection_id: string };
      const col = state.byId[collection_id] as Collection;

      if (col) {
        const newImages = [...col.images, image];

        return {
          ...state,
          byId: { ...state.byId, [collection_id]: { ...col, images: newImages } }
        };
      }

      return state;
    }
    case 'UPDATE_COLLECTION_BY_PATH': {
      let { path } = action.payload as { path: string; collection_id: string; value: unknown }; // eslint-disable-line
      const { collection_id, value } = action.payload as {
        path: string;
        collection_id: string;
        value: unknown;
      };
      const col = state.byId[collection_id] as Collection;

      if (col) {
        while (path.includes('[')) {
          path = path.replace('[', '.').replace(']', '');
        }

        const updatedCol = dotProp.set(col, path, value);

        return { ...state, byId: { ...state.byId, [collection_id]: updatedCol } };
      }

      return state;
    }
    case 'ORDER_COLLECTION_GALLERYS': {
      const { collection_id, sortedGallerys } = action.payload as {
        collection_id: string;
        sortedGallerys: Gallery[];
      };
      const col = state.byId[collection_id] as Collection;

      if (col) {
        const updatedCol = dotProp.set(col, 'galleries', sortedGallerys);

        return { ...state, byId: { ...state.byId, [collection_id]: updatedCol } };
      }

      return state;
    }
    case 'ADD_GALLERY_TO_COLLECTION': {
      const { collection_id, gallery } = action.payload as {
        collection_id: string;
        gallery: Gallery;
      };
      const col = state.byId[collection_id] as Collection;

      if (col) {
        let gallerys: Gallery[] = [];

        if (col.galleries) gallerys = [...col.galleries];
        gallerys.push(gallery);

        const updatedCol = dotProp.set(col, 'galleries', gallerys);

        return { ...state, byId: { ...state.byId, [collection_id]: updatedCol } };
      }

      return state;
    }
    case 'DELETE_GALLERY_FROM_COLLECTION': {
      const { collection_id, gallery_id } = action.payload as {
        collection_id: string;
        gallery_id: string;
      };
      const col = state.byId[collection_id] as Collection;

      if (col) {
        const updatedCol = dotProp.set(
          col,
          'galleries',
          col.galleries.filter((g: Gallery) => g._id !== gallery_id)
        );

        return { ...state, byId: { ...state.byId, [collection_id]: updatedCol } };
      }

      return state;
    }
    default:
      return state;
  }
}
